import * as THREE from 'three';
import { CAR, MAP } from '../utils/constants';
import { getBuildingAABBs, resolveCircleAABB, type AABBXZ } from './collision';
import { mulberry32 } from '../utils/rng';

export type DestructibleKind =
  | 'cone'
  | 'barrier'
  | 'hydrant'
  | 'newsstand'
  | 'trash'
  | 'vendor_cart'
  | 'cafe_set'
  | 'gas_station'
  | 'water_tower'
  | 'scaffolding'
  | 'billboard'
  | 'container_stack'
  | 'garage_gate';

export type DestructibleInstance = {
  kind: DestructibleKind;
  x: number;
  z: number;
  radius: number;
  health: number;
  maxHealth: number;
  /** Slowdown 0–1 applied to velocity when hitting barrier. */
  slowdown: number;
  alive: boolean;
  root: THREE.Group;
  /** Cone / barrier tip animation. */
  tipped: boolean;
  /** Damage once per overlap episode (no per-frame melt). */
  touchingCar: boolean;
};

export type DestructibleHitEvent = {
  kind: DestructibleKind;
  x: number;
  z: number;
  destroyed: boolean;
  waterBurst: boolean;
  pursuitBreaker: boolean;
  shockRadius: number;
  /** Rough “damage bill” for stats — feels like $ chaos, not a +1 counter. */
  chaosBill: number;
};

const CONE_R = 0.55;
const BARRIER_R = 1.35;
const HYDRANT_R = 0.62;
const NEWS_R = 0.92;
const TRASH_R = 0.48;
const VENDOR_R = 1.05;
const CAFE_R = 1.15;
const GAS_STATION_R = 2.9;
const WATER_TOWER_R = 2.5;
const SCAFFOLD_R = 2.2;
const BILLBOARD_R = 2.2;
const CONTAINER_STACK_R = 2.1;
const GARAGE_GATE_R = 2.3;

function isInsideAnyBuilding(x: number, z: number, r: number, boxes: AABBXZ[]): boolean {
  for (const b of boxes) {
    const res = resolveCircleAABB(x, z, r, b);
    if (res) return true;
  }
  return false;
}

// ═══════════════ SHARED MATERIALS (reduce allocations) ═══════════════
const _mat = {
  chrome: new THREE.MeshStandardMaterial({ color: 0xc0c4cc, roughness: 0.25, metalness: 0.7 }),
  darkMetal: new THREE.MeshStandardMaterial({ color: 0x4a4e58, roughness: 0.45, metalness: 0.65 }),
  steel: new THREE.MeshStandardMaterial({ color: 0x707880, roughness: 0.4, metalness: 0.6 }),
  steelWhite: new THREE.MeshStandardMaterial({ color: 0xe8e8ec, roughness: 0.35, metalness: 0.55 }),
  rubber: new THREE.MeshStandardMaterial({ color: 0x1a1a1e, roughness: 0.92, metalness: 0.04 }),
  wood: new THREE.MeshStandardMaterial({ color: 0x7a5238, roughness: 0.72, metalness: 0.05 }),
  woodLight: new THREE.MeshStandardMaterial({ color: 0xa07850, roughness: 0.68, metalness: 0.04 }),
  concrete: new THREE.MeshStandardMaterial({ color: 0xd0ccc4, roughness: 0.8, metalness: 0.05 }),
  concreteDark: new THREE.MeshStandardMaterial({ color: 0x8a8880, roughness: 0.75, metalness: 0.08 }),
  brick: new THREE.MeshStandardMaterial({ color: 0x8b5e3c, roughness: 0.85, metalness: 0.05 }),
  plastic: new THREE.MeshStandardMaterial({ color: 0x2a2a30, roughness: 0.6, metalness: 0.1 }),
  fabric: new THREE.MeshStandardMaterial({ color: 0xcc3333, roughness: 0.85, metalness: 0.02 }),
  yellow: new THREE.MeshStandardMaterial({ color: 0xddc820, roughness: 0.5, metalness: 0.1, emissive: 0x886610, emissiveIntensity: 0.1 }),
};

function makeConeMesh(): THREE.Group {
  const g = new THREE.Group();
  const orange = new THREE.MeshStandardMaterial({
    color: 0xff6a00, roughness: 0.55, metalness: 0.08,
    emissive: 0x331100, emissiveIntensity: 0.12,
  });
  const reflective = new THREE.MeshStandardMaterial({
    color: 0xf0f0f0, roughness: 0.15, metalness: 0.4,
    emissive: 0xcccccc, emissiveIntensity: 0.15,
  });

  // Rubber base
  const base = new THREE.Mesh(
    new THREE.BoxGeometry(0.72, 0.06, 0.72),
    _mat.rubber,
  );
  base.position.y = 0.03;
  g.add(base);

  // Main cone body (lower)
  const lower = new THREE.Mesh(new THREE.ConeGeometry(0.34, 0.45, 10), orange);
  lower.position.y = 0.28;
  g.add(lower);

  // Reflective stripe band (lower)
  const stripe1 = new THREE.Mesh(
    new THREE.CylinderGeometry(0.22, 0.28, 0.1, 10),
    reflective,
  );
  stripe1.position.y = 0.32;
  g.add(stripe1);

  // Upper cone
  const upper = new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.42, 10), orange);
  upper.position.y = 0.68;
  g.add(upper);

  // Reflective stripe band (upper)
  const stripe2 = new THREE.Mesh(
    new THREE.CylinderGeometry(0.1, 0.14, 0.08, 10),
    reflective,
  );
  stripe2.position.y = 0.62;
  g.add(stripe2);

  // Tip
  const tip = new THREE.Mesh(
    new THREE.SphereGeometry(0.04, 6, 4),
    orange,
  );
  tip.position.y = 0.92;
  g.add(tip);

  return g;
}

function makeBarrierMesh(): THREE.Group {
  const g = new THREE.Group();
  const orangeStripe = new THREE.MeshStandardMaterial({
    color: 0xff8830, roughness: 0.5, emissive: 0x443300, emissiveIntensity: 0.08,
  });
  const whiteStripe = new THREE.MeshStandardMaterial({
    color: 0xf0f0f0, roughness: 0.45, metalness: 0.05,
  });

  // A-frame legs
  for (const xSide of [-0.85, 0.85]) {
    const legOuter = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.9, 0.06), _mat.steel);
    legOuter.position.set(xSide, 0.45, -0.22);
    legOuter.rotation.z = xSide > 0 ? -0.06 : 0.06;
    g.add(legOuter);
    const legInner = legOuter.clone();
    legInner.position.z = 0.22;
    g.add(legInner);
    // Cross brace
    const brace = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.04, 0.4), _mat.steel);
    brace.position.set(xSide, 0.25, 0);
    g.add(brace);
    // Foot plates
    const foot = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.04, 0.5), _mat.darkMetal);
    foot.position.set(xSide, 0.02, 0);
    g.add(foot);
  }

  // Main board (alternating orange/white stripes)
  const board = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.22, 0.06), orangeStripe);
  board.position.set(0, 0.82, 0);
  g.add(board);
  const stripe2 = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.18, 0.065), whiteStripe);
  stripe2.position.set(0, 0.62, 0);
  g.add(stripe2);

  // Reflective dots on board
  const dotMat = new THREE.MeshStandardMaterial({
    color: 0xffee88, roughness: 0.15, metalness: 0.3,
    emissive: 0xffcc00, emissiveIntensity: 0.2,
  });
  for (let dx = -0.8; dx <= 0.8; dx += 0.4) {
    const dot = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.01, 6), dotMat);
    dot.rotation.x = Math.PI / 2;
    dot.position.set(dx, 0.82, 0.04);
    g.add(dot);
  }

  // Warning light on top (orange blinker)
  const blinker = new THREE.Mesh(
    new THREE.SphereGeometry(0.06, 6, 6),
    new THREE.MeshStandardMaterial({
      color: 0xff8800, emissive: 0xff6600, emissiveIntensity: 0.6,
    }),
  );
  blinker.position.set(0, 0.98, 0);
  g.add(blinker);

  return g;
}

function makeNewsstandMesh(): THREE.Group {
  const g = new THREE.Group();
  const greenPaint = new THREE.MeshStandardMaterial({
    color: 0x2a5a38, roughness: 0.55, metalness: 0.12,
  });
  const awningMat = new THREE.MeshStandardMaterial({
    color: 0xd44c6a, emissive: 0x441020, emissiveIntensity: 0.08, roughness: 0.45,
  });
  const paper = new THREE.MeshStandardMaterial({ color: 0xe8e4dc, roughness: 0.75 });
  const magazine = new THREE.MeshStandardMaterial({ color: 0xcc3344, roughness: 0.65 });
  const magBlue = new THREE.MeshStandardMaterial({ color: 0x3355aa, roughness: 0.65 });

  // Base counter
  const counter = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.08, 0.75), greenPaint);
  counter.position.y = 0.96;
  g.add(counter);

  // Back wall
  const backWall = new THREE.Mesh(new THREE.BoxGeometry(1.8, 1.3, 0.06), greenPaint);
  backWall.position.set(0, 0.65, -0.35);
  g.add(backWall);

  // Side walls
  for (const sx of [-0.88, 0.88]) {
    const side = new THREE.Mesh(new THREE.BoxGeometry(0.06, 1.3, 0.72), greenPaint);
    side.position.set(sx, 0.65, 0);
    g.add(side);
  }

  // Shelves inside (3 tiers)
  for (const sy of [0.3, 0.55, 0.78]) {
    const shelf = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.04, 0.65), _mat.wood);
    shelf.position.set(0, sy, -0.02);
    g.add(shelf);
  }

  // Magazine/newspaper stacks on display
  for (let col = -0.55; col <= 0.55; col += 0.35) {
    const mat = col < 0 ? magazine : col > 0.2 ? magBlue : paper;
    const stack = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.15, 0.2), mat);
    stack.position.set(col, 1.04, 0.15);
    stack.rotation.z = (col * 0.1);
    g.add(stack);
  }

  // Paper stack on counter
  const papers = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.12, 0.35), paper);
  papers.position.set(-0.4, 1.08, 0.1);
  g.add(papers);

  // Awning (sloped)
  const awning = new THREE.Mesh(new THREE.BoxGeometry(2.0, 0.06, 1.0), awningMat);
  awning.position.set(0, 1.35, 0.12);
  awning.rotation.x = 0.15;
  g.add(awning);

  // Awning valance (scalloped edge hint)
  const valance = new THREE.Mesh(new THREE.BoxGeometry(2.0, 0.1, 0.03), awningMat);
  valance.position.set(0, 1.28, 0.6);
  g.add(valance);

  // Support poles for awning
  for (const px of [-0.85, 0.85]) {
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.45, 4), _mat.steel);
    pole.position.set(px, 1.12, 0.55);
    g.add(pole);
  }

  return g;
}

function makeTrashMesh(): THREE.Group {
  const g = new THREE.Group();
  const metalGreen = new THREE.MeshStandardMaterial({
    color: 0x3a5e44, roughness: 0.65, metalness: 0.2,
  });

  // Main cylindrical body with slight taper
  const body = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.36, 0.8, 10), metalGreen);
  body.position.y = 0.4;
  g.add(body);

  // Decorative ribs
  for (const ry of [0.2, 0.5, 0.7]) {
    const rib = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.015, 4, 14), _mat.darkMetal);
    rib.rotation.x = Math.PI / 2;
    rib.position.y = ry;
    g.add(rib);
  }

  // Top rim
  const rim = new THREE.Mesh(new THREE.TorusGeometry(0.34, 0.025, 6, 14), _mat.darkMetal);
  rim.rotation.x = Math.PI / 2;
  rim.position.y = 0.82;
  g.add(rim);

  // Dome lid
  const lid = new THREE.Mesh(
    new THREE.SphereGeometry(0.33, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.4),
    metalGreen,
  );
  lid.position.y = 0.82;
  g.add(lid);

  // Swing flap opening
  const flap = new THREE.Mesh(
    new THREE.BoxGeometry(0.2, 0.12, 0.06),
    _mat.plastic,
  );
  flap.position.set(0, 0.9, 0.28);
  g.add(flap);

  // Base ring
  const baseRing = new THREE.Mesh(
    new THREE.TorusGeometry(0.37, 0.02, 4, 14),
    _mat.darkMetal,
  );
  baseRing.rotation.x = Math.PI / 2;
  baseRing.position.y = 0.02;
  g.add(baseRing);

  return g;
}

function makeHydrantMesh(): THREE.Group {
  const g = new THREE.Group();
  const redPaint = new THREE.MeshStandardMaterial({
    color: 0xcc2233, roughness: 0.42, metalness: 0.28,
    emissive: 0x220808, emissiveIntensity: 0.08,
  });
  const darkRed = new THREE.MeshStandardMaterial({ color: 0x881520, roughness: 0.55, metalness: 0.3 });
  const brass = new THREE.MeshStandardMaterial({ color: 0xc8a840, roughness: 0.35, metalness: 0.7 });

  // Base plate
  const basePlate = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.31, 0.06, 12), _mat.darkMetal);
  basePlate.position.y = 0.03;
  g.add(basePlate);
  const baseRing = new THREE.Mesh(new THREE.TorusGeometry(0.285, 0.018, 6, 16), _mat.darkMetal);
  baseRing.rotation.x = Math.PI / 2;
  baseRing.position.y = 0.06;
  g.add(baseRing);

  // Lower barrel (tapered)
  const lower = new THREE.Mesh(new THREE.CylinderGeometry(0.175, 0.22, 0.32, 12), redPaint);
  lower.position.y = 0.22;
  g.add(lower);
  for (const y of [0.12, 0.34]) {
    const ring = new THREE.Mesh(new THREE.TorusGeometry(0.19, 0.012, 6, 14), darkRed);
    ring.rotation.x = Math.PI / 2;
    ring.position.y = y;
    g.add(ring);
  }

  // Main body
  const mainBody = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.175, 0.22, 12), redPaint);
  mainBody.position.y = 0.49;
  g.add(mainBody);

  // Side nozzle outlets (left + right)
  for (const side of [-1, 1]) {
    const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.065, 0.065, 0.16, 8), redPaint);
    pipe.rotation.z = Math.PI / 2;
    pipe.position.set(side * 0.22, 0.46, 0);
    g.add(pipe);
    const flange = new THREE.Mesh(new THREE.CylinderGeometry(0.085, 0.085, 0.035, 10), darkRed);
    flange.rotation.z = Math.PI / 2;
    flange.position.set(side * 0.30, 0.46, 0);
    g.add(flange);
    const cap = new THREE.Mesh(new THREE.CylinderGeometry(0.058, 0.058, 0.025, 5), _mat.chrome);
    cap.rotation.z = Math.PI / 2;
    cap.position.set(side * 0.325, 0.46, 0);
    g.add(cap);
  }

  // Bonnet (upper taper)
  const bonnet = new THREE.Mesh(new THREE.CylinderGeometry(0.135, 0.18, 0.14, 12), redPaint);
  bonnet.position.y = 0.67;
  g.add(bonnet);

  // Dome top
  const topCap = new THREE.Mesh(
    new THREE.SphereGeometry(0.135, 12, 6, 0, Math.PI * 2, 0, Math.PI * 0.55),
    redPaint,
  );
  topCap.position.y = 0.74;
  g.add(topCap);

  // Pentagon operating nut
  const pentNut = new THREE.Mesh(new THREE.CylinderGeometry(0.048, 0.048, 0.06, 5), brass);
  pentNut.position.y = 0.81;
  g.add(pentNut);

  // Front nozzle (smaller)
  const frontPipe = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 0.1, 6), redPaint);
  frontPipe.rotation.x = Math.PI / 2;
  frontPipe.position.set(0, 0.34, 0.2);
  g.add(frontPipe);
  const frontFlange = new THREE.Mesh(new THREE.CylinderGeometry(0.062, 0.062, 0.025, 8), darkRed);
  frontFlange.rotation.x = Math.PI / 2;
  frontFlange.position.set(0, 0.34, 0.255);
  g.add(frontFlange);

  return g;
}

function makeVendorCartMesh(): THREE.Group {
  const g = new THREE.Group();
  const bodyMat = new THREE.MeshStandardMaterial({
    color: 0xc84828, roughness: 0.55, metalness: 0.15,
    emissive: 0x331000, emissiveIntensity: 0.1,
  });
  const stainless = new THREE.MeshStandardMaterial({
    color: 0xccccdd, roughness: 0.25, metalness: 0.7,
  });
  const umbrellaMat = new THREE.MeshStandardMaterial({
    color: 0xffdd22, roughness: 0.6, metalness: 0.02,
    emissive: 0xffaa00, emissiveIntensity: 0.15,
  });
  const umbrellaStripe = new THREE.MeshStandardMaterial({
    color: 0x2266cc, roughness: 0.6, metalness: 0.02,
  });

  // Wheels (front pair is smaller, single rear wheel on push handle side)
  const wheelGeo = new THREE.CylinderGeometry(0.18, 0.18, 0.1, 10);
  for (const [x, z] of [[-0.7, -0.35], [-0.7, 0.35]] as [number, number][]) {
    const w = new THREE.Mesh(wheelGeo, _mat.rubber);
    w.rotation.z = Math.PI / 2;
    w.position.set(x, 0.18, z);
    g.add(w);
    // Wheel hub
    const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.12, 6), _mat.chrome);
    hub.rotation.z = Math.PI / 2;
    hub.position.set(x, 0.18, z);
    g.add(hub);
  }

  // Cart body
  const body = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.65, 0.9), bodyMat);
  body.position.set(-0.1, 0.62, 0);
  g.add(body);

  // Stainless steel top surface (cooking area)
  const top = new THREE.Mesh(new THREE.BoxGeometry(1.65, 0.04, 0.92), stainless);
  top.position.set(-0.1, 0.96, 0);
  g.add(top);

  // Serving window / glass shield (front)
  const glass = new THREE.Mesh(
    new THREE.BoxGeometry(1.2, 0.35, 0.03),
    new THREE.MeshStandardMaterial({
      color: 0x88ccee, roughness: 0.1, transparent: true, opacity: 0.3,
    }),
  );
  glass.position.set(-0.1, 1.16, 0.46);
  g.add(glass);

  // Glass frame
  const glassFrame = new THREE.Mesh(new THREE.BoxGeometry(1.22, 0.37, 0.02), _mat.steel);
  glassFrame.position.set(-0.1, 1.16, 0.465);
  g.add(glassFrame);

  // Push handle
  const handleBar = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.7, 6), _mat.steel);
  handleBar.position.set(0.85, 0.85, 0);
  handleBar.rotation.z = -0.4;
  g.add(handleBar);
  const handleGrip = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.032, 0.55, 6), _mat.rubber);
  handleGrip.position.set(1.0, 1.1, 0);
  g.add(handleGrip);

  // Umbrella pole
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 1.0, 6), _mat.steel);
  pole.position.set(-0.1, 1.46, 0);
  g.add(pole);

  // Umbrella canopy (alternating colored panels)
  const umbrellaR = 0.65;
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    const nextAngle = ((i + 1) / 8) * Math.PI * 2;
    const geo = new THREE.BufferGeometry();
    const cx = -0.1, cy = 1.96, cz = 0;
    const verts = new Float32Array([
      cx, cy, cz,
      cx + Math.sin(angle) * umbrellaR, cy - 0.15, cz + Math.cos(angle) * umbrellaR,
      cx + Math.sin(nextAngle) * umbrellaR, cy - 0.15, cz + Math.cos(nextAngle) * umbrellaR,
    ]);
    geo.setAttribute('position', new THREE.BufferAttribute(verts, 3));
    geo.computeVertexNormals();
    const panel = new THREE.Mesh(geo, i % 2 === 0 ? umbrellaMat : umbrellaStripe);
    g.add(panel);
  }

  // Condiment bottles on top
  for (const [bx, col] of [[-0.4, 0xcc2222], [-0.25, 0xddcc22], [-0.1, 0x22aa44]] as [number, number][]) {
    const bottle = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.03, 0.12, 6),
      new THREE.MeshStandardMaterial({ color: col, roughness: 0.4 }));
    bottle.position.set(bx, 1.04, -0.2);
    g.add(bottle);
  }

  return g;
}

function makeCafeSetMesh(): THREE.Group {
  const g = new THREE.Group();
  const wroughtIron = new THREE.MeshStandardMaterial({
    color: 0x2a2e35, roughness: 0.55, metalness: 0.5,
  });
  const marbleTop = new THREE.MeshStandardMaterial({
    color: 0xe8e4e0, roughness: 0.35, metalness: 0.05,
  });
  const fabricRed = new THREE.MeshStandardMaterial({
    color: 0xbb3333, roughness: 0.82, metalness: 0.02,
  });
  const umbrellaGreen = new THREE.MeshStandardMaterial({
    color: 0x2a6638, roughness: 0.6, metalness: 0.02,
  });

  // Table — round marble top on iron pedestal
  const tableTop = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.48, 0.05, 14), marbleTop);
  tableTop.position.y = 0.74;
  g.add(tableTop);
  const tableEdge = new THREE.Mesh(new THREE.TorusGeometry(0.48, 0.015, 4, 14), wroughtIron);
  tableEdge.rotation.x = Math.PI / 2;
  tableEdge.position.y = 0.74;
  g.add(tableEdge);
  const pedestal = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.68, 6), wroughtIron);
  pedestal.position.y = 0.37;
  g.add(pedestal);
  const pedestalBase = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.3, 0.04, 4), wroughtIron);
  pedestalBase.position.y = 0.02;
  g.add(pedestalBase);
  // Pedestal feet (X-shape)
  for (let a = 0; a < 4; a++) {
    const foot = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.025, 0.04), wroughtIron);
    foot.rotation.y = (a / 4) * Math.PI * 2;
    foot.position.y = 0.015;
    g.add(foot);
  }

  // Umbrella
  const uPole = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 1.1, 4), wroughtIron);
  uPole.position.y = 1.3;
  g.add(uPole);
  const uCanopy = new THREE.Mesh(
    new THREE.ConeGeometry(0.75, 0.2, 8, 1, true),
    umbrellaGreen,
  );
  uCanopy.position.y = 1.82;
  g.add(uCanopy);
  const uTop = new THREE.Mesh(new THREE.SphereGeometry(0.04, 6, 4), wroughtIron);
  uTop.position.y = 1.9;
  g.add(uTop);

  // Two bistro chairs
  for (const [cx, cz, rot] of [[0.72, 0, 0], [-0.72, 0, Math.PI]] as [number, number, number][]) {
    // Seat
    const seat = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.04, 10), fabricRed);
    seat.position.set(cx, 0.46, cz);
    g.add(seat);
    // Back rest (curved)
    const back = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.2, 0.32, 10, 1, false, -0.6, 1.2), wroughtIron);
    back.position.set(cx, 0.6, cz - 0.12 * Math.cos(rot) - 0.12 * (rot === 0 ? -1 : 1));
    g.add(back);
    // 4 legs
    for (let li = 0; li < 4; li++) {
      const la = (li / 4) * Math.PI * 2;
      const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.44, 4), wroughtIron);
      leg.position.set(
        cx + Math.sin(la) * 0.15,
        0.22,
        cz + Math.cos(la) * 0.15,
      );
      leg.rotation.x = Math.cos(la) * 0.08;
      leg.rotation.z = Math.sin(la) * 0.08;
      g.add(leg);
    }
  }

  // Cup on table
  const cup = new THREE.Mesh(
    new THREE.CylinderGeometry(0.03, 0.025, 0.06, 6),
    new THREE.MeshStandardMaterial({ color: 0xf0f0f0, roughness: 0.3 }),
  );
  cup.position.set(0.1, 0.8, 0.05);
  g.add(cup);

  return g;
}

function makeGasStationMesh(): THREE.Group {
  const g = new THREE.Group();
  const redAccent = new THREE.MeshStandardMaterial({
    color: 0xcc2020, roughness: 0.45, metalness: 0.2,
    emissive: 0x440505, emissiveIntensity: 0.25,
  });
  const greenPump = new THREE.MeshStandardMaterial({ color: 0x28884a, roughness: 0.45, metalness: 0.2 });
  const glowScreen = new THREE.MeshStandardMaterial({
    color: 0x22ff66, emissive: 0x22ff66, emissiveIntensity: 0.6, roughness: 0.3,
  });

  // Concrete island curb
  const island = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.16, 3.2), _mat.concrete);
  island.position.y = 0.08;
  g.add(island);

  // Yellow safety stripes
  for (let zi = -1.2; zi <= 1.2; zi += 0.5) {
    const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.01, 0.1), _mat.yellow);
    stripe.position.set(0, 0.17, zi);
    g.add(stripe);
  }

  // 2 fuel pumps on island
  for (const pz of [-0.7, 0.7]) {
    // Pump body
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.45, 1.2, 0.3), greenPump);
    body.position.set(0, 0.76, pz);
    g.add(body);
    // Top cap
    const cap = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.06, 0.34), _mat.steel);
    cap.position.set(0, 1.39, pz);
    g.add(cap);
    // Screen
    const screen = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.22, 0.04), _mat.plastic);
    screen.position.set(0, 1.05, pz + 0.17);
    g.add(screen);
    // Digits
    for (let d = 0; d < 3; d++) {
      const digit = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.08, 0.01), glowScreen);
      digit.position.set(-0.08 + d * 0.08, 1.08, pz + 0.2);
      g.add(digit);
    }
    // Nozzle holsters (both sides)
    for (const ns of [-1, 1]) {
      const holster = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.12, 0.1), _mat.steel);
      holster.position.set(ns * 0.26, 0.85, pz);
      g.add(holster);
      const nozzle = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.015, 0.14, 5), _mat.darkMetal);
      nozzle.rotation.z = ns * 0.3;
      nozzle.position.set(ns * 0.28, 0.78, pz);
      g.add(nozzle);
    }
    // Grade buttons
    for (let bi = 0; bi < 3; bi++) {
      const cols = [0x22aa44, 0xddcc22, 0xcc2222];
      const btn = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.015, 6),
        new THREE.MeshStandardMaterial({ color: cols[bi], emissive: cols[bi], emissiveIntensity: 0.3 }));
      btn.rotation.x = Math.PI / 2;
      btn.position.set(-0.06 + bi * 0.06, 1.2, pz + 0.17);
      g.add(btn);
    }
  }

  // Bollards
  for (const bz of [-1.4, 1.4]) {
    const bollard = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.6, 6), _mat.yellow);
    bollard.position.set(0, 0.3, bz);
    g.add(bollard);
    const bCap = new THREE.Mesh(new THREE.SphereGeometry(0.07, 6, 3, 0, Math.PI * 2, 0, Math.PI * 0.5), _mat.yellow);
    bCap.position.set(0, 0.6, bz);
    g.add(bCap);
  }

  // Small canopy over pumps (4 pillars + roof)
  const canopyW = 2.4, canopyD = 3.8, canopyH = 3.6;
  for (const [px, pz] of [[-1.0, -1.5], [1.0, -1.5], [-1.0, 1.5], [1.0, 1.5]] as [number, number][]) {
    const pillar = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.1, canopyH, 6), _mat.steelWhite);
    pillar.position.set(px, canopyH / 2, pz);
    g.add(pillar);
  }
  const roof = new THREE.Mesh(new THREE.BoxGeometry(canopyW + 0.4, 0.2, canopyD + 0.2), _mat.steelWhite);
  roof.position.y = canopyH + 0.1;
  g.add(roof);
  // Red fascia
  for (const fz of [-(canopyD + 0.2) / 2, (canopyD + 0.2) / 2]) {
    const fascia = new THREE.Mesh(new THREE.BoxGeometry(canopyW + 0.5, 0.2, 0.06), redAccent);
    fascia.position.set(0, canopyH - 0.02, fz);
    g.add(fascia);
  }
  // Under-canopy lights
  for (const lz of [-0.6, 0.6]) {
    const light = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.03, 0.4),
      new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0xffeedd, emissiveIntensity: 0.7 }));
    light.position.set(0, canopyH - 0.02, lz);
    g.add(light);
  }

  return g;
}

function makeWaterTowerBreakerMesh(): THREE.Group {
  const g = new THREE.Group();
  const legMat = new THREE.MeshStandardMaterial({ color: 0x8d96a3, metalness: 0.35, roughness: 0.52 });
  const woodStave = new THREE.MeshStandardMaterial({ color: 0x8b6840, roughness: 0.75, metalness: 0.04 });
  const metalBand = new THREE.MeshStandardMaterial({ color: 0x606870, roughness: 0.4, metalness: 0.55 });
  const roofMat = new THREE.MeshStandardMaterial({ color: 0x556060, roughness: 0.65, metalness: 0.2 });

  // 4 steel legs with slight outward splay
  const legH = 3.2;
  for (const [x, z] of [[-1.2, -1.2], [1.2, -1.2], [-1.2, 1.2], [1.2, 1.2]] as [number, number][]) {
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.18, legH, 6), legMat);
    leg.position.set(x * 1.05, legH / 2, z * 1.05);
    g.add(leg);
    // Foot plate
    const foot = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.06, 0.4), _mat.darkMetal);
    foot.position.set(x * 1.05, 0.03, z * 1.05);
    g.add(foot);
  }

  // Cross braces (X pattern on 2 sides)
  for (const pair of [[[-1.25, -1.25], [1.25, -1.25]], [[-1.25, 1.25], [1.25, 1.25]]] as [number, number][][]) {
    const [p1, p2] = pair;
    const midX = (p1[0] + p2[0]) / 2;
    const midZ = (p1[1] + p2[1]) / 2;
    const len = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]) * 1.2;
    const brace = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, len, 4), legMat);
    brace.position.set(midX, 1.6, midZ);
    brace.rotation.z = 0.55;
    g.add(brace);
    const brace2 = brace.clone();
    brace2.rotation.z = -0.55;
    g.add(brace2);
  }

  // Horizontal ring braces
  for (const h of [1.0, 2.2]) {
    for (const pair of [[[-1.25, -1.25], [1.25, -1.25]], [[1.25, -1.25], [1.25, 1.25]],
                         [[1.25, 1.25], [-1.25, 1.25]], [[-1.25, 1.25], [-1.25, -1.25]]] as [number, number][][]) {
      const [p1, p2] = pair;
      const len = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
      const rail = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, len, 4), legMat);
      rail.position.set((p1[0] + p2[0]) / 2, h, (p1[1] + p2[1]) / 2);
      rail.rotation.z = Math.PI / 2;
      if (p1[0] === p2[0]) rail.rotation.y = Math.PI / 2;
      g.add(rail);
    }
  }

  // Wooden tank (stave barrel shape)
  const tankY = legH + 0.8;
  const tankR = 1.5, tankH = 1.8;
  const tank = new THREE.Mesh(new THREE.CylinderGeometry(tankR, tankR * 1.08, tankH, 16), woodStave);
  tank.position.y = tankY;
  g.add(tank);

  // Metal bands around tank
  for (const by of [-0.55, 0, 0.55]) {
    const band = new THREE.Mesh(new THREE.TorusGeometry(tankR + 0.02, 0.04, 4, 18), metalBand);
    band.rotation.x = Math.PI / 2;
    band.position.y = tankY + by;
    g.add(band);
  }

  // Conical roof
  const roof = new THREE.Mesh(new THREE.ConeGeometry(tankR + 0.15, 0.7, 12), roofMat);
  roof.position.y = tankY + tankH / 2 + 0.35;
  g.add(roof);

  // Roof finial
  const finial = new THREE.Mesh(new THREE.SphereGeometry(0.08, 6, 4), _mat.chrome);
  finial.position.y = tankY + tankH / 2 + 0.75;
  g.add(finial);

  // Pipe going down one side
  const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, legH + 0.5, 6), legMat);
  pipe.position.set(1.3, (legH + 0.5) / 2, 0);
  g.add(pipe);

  return g;
}

function makeScaffoldingMesh(): THREE.Group {
  const g = new THREE.Group();
  const frameMat = new THREE.MeshStandardMaterial({ color: 0xb6bbc6, metalness: 0.58, roughness: 0.35 });
  const plankMat = new THREE.MeshStandardMaterial({ color: 0x8d623b, roughness: 0.72 });
  const meshMat = new THREE.MeshStandardMaterial({
    color: 0x226622, roughness: 0.8, transparent: true, opacity: 0.4, side: THREE.DoubleSide,
  });

  // 4 corner uprights (tall)
  const uprightH = 4.8;
  for (const x of [-1.2, 1.2]) {
    for (const z of [-0.6, 0.6]) {
      const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, uprightH, 4), frameMat);
      pole.position.set(x, uprightH / 2, z);
      g.add(pole);
      // Base jack (adjustable foot)
      const jack = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.12, 0.15, 6), _mat.darkMetal);
      jack.position.set(x, 0.075, z);
      g.add(jack);
    }
  }

  // Horizontal ledgers at each level
  for (const h of [0.5, 1.5, 2.8, 4.2]) {
    // Long side rails
    for (const z of [-0.6, 0.6]) {
      const rail = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 2.4, 4), frameMat);
      rail.rotation.z = Math.PI / 2;
      rail.position.set(0, h, z);
      g.add(rail);
    }
    // Short side rails
    for (const x of [-1.2, 1.2]) {
      const rail = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.035, 1.2, 4), frameMat);
      rail.rotation.x = Math.PI / 2;
      rail.position.set(x, h, 0);
      g.add(rail);
    }
  }

  // Diagonal cross braces on long sides
  for (const z of [-0.6, 0.6]) {
    for (const level of [0, 1]) {
      const baseH = level * 2.1 + 0.5;
      const brace = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 3.2, 3), frameMat);
      brace.position.set(0, baseH + 1.05, z);
      brace.rotation.z = 0.72;
      g.add(brace);
    }
  }

  // Planks at 2 levels
  for (const h of [1.5, 2.8]) {
    for (let px = -1.0; px <= 1.0; px += 0.5) {
      const plank = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.06, 1.15), plankMat);
      plank.position.set(px, h + 0.03, 0);
      g.add(plank);
    }
  }

  // Top plank platform
  for (let px = -1.0; px <= 1.0; px += 0.5) {
    const plank = new THREE.Mesh(new THREE.BoxGeometry(0.45, 0.06, 1.15), plankMat);
    plank.position.set(px, 4.23, 0);
    g.add(plank);
  }

  // Toe boards at platform levels
  for (const h of [1.55, 2.85]) {
    const toe = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.15, 0.03), plankMat);
    toe.position.set(0, h + 0.08, 0.6);
    g.add(toe);
  }

  // Safety mesh on front face
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2.4, 3.8), meshMat);
  mesh.position.set(0, 2.4, 0.62);
  g.add(mesh);

  // Caution tape
  const tape = new THREE.Mesh(
    new THREE.BoxGeometry(2.6, 0.15, 0.02),
    new THREE.MeshBasicMaterial({ color: 0xffdd00 }),
  );
  tape.position.set(0, 0.9, 0.65);
  g.add(tape);
  const tape2 = tape.clone();
  tape2.position.y = 1.2;
  g.add(tape2);

  return g;
}

function makeBillboardMesh(): THREE.Group {
  const g = new THREE.Group();
  const poleMat = new THREE.MeshStandardMaterial({ color: 0x636c7a, roughness: 0.55, metalness: 0.32 });
  const adMat = new THREE.MeshStandardMaterial({
    color: 0x4ed1ff, emissive: 0x00aaff, emissiveIntensity: 0.45, roughness: 0.35,
  });
  const neonBorder = new THREE.MeshStandardMaterial({
    color: 0xff44cc, emissive: 0xff22aa, emissiveIntensity: 0.8,
  });

  // Main support pole (I-beam style)
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.28, 4.0, 8), poleMat);
  pole.position.y = 2.0;
  g.add(pole);
  // Pole base plate
  const basePlate = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.1, 1.2), _mat.darkMetal);
  basePlate.position.y = 0.05;
  g.add(basePlate);
  // Anchor bolts at base
  for (const [bx, bz] of [[-0.4, -0.4], [0.4, -0.4], [-0.4, 0.4], [0.4, 0.4]] as [number, number][]) {
    const bolt = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 0.08, 6), _mat.chrome);
    bolt.position.set(bx, 0.14, bz);
    g.add(bolt);
  }

  // Ad panel face
  const board = new THREE.Mesh(new THREE.BoxGeometry(4.8, 2.2, 0.12), adMat);
  board.position.y = 4.2;
  g.add(board);

  // Back of billboard (exposed structure)
  const backPanel = new THREE.Mesh(
    new THREE.BoxGeometry(4.8, 2.2, 0.05),
    new THREE.MeshStandardMaterial({ color: 0x444444, roughness: 0.7, metalness: 0.2 }),
  );
  backPanel.position.set(0, 4.2, -0.1);
  g.add(backPanel);

  // Structural angle supports (triangular bracing from pole to board)
  for (const sx of [-1.5, 1.5]) {
    const support = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 2.2, 4), poleMat);
    support.rotation.z = sx > 0 ? -0.6 : 0.6;
    support.position.set(sx * 0.5, 3.0, -0.06);
    g.add(support);
  }

  // Horizontal back support beams
  for (const by of [3.4, 4.2, 5.0]) {
    const beam = new THREE.Mesh(new THREE.BoxGeometry(4.6, 0.08, 0.08), poleMat);
    beam.position.set(0, by, -0.12);
    g.add(beam);
  }

  // Neon border strips (top and bottom)
  const topBar = new THREE.Mesh(new THREE.BoxGeometry(5.0, 0.1, 0.18), neonBorder);
  topBar.position.y = 5.35;
  g.add(topBar);
  const bottomBar = new THREE.Mesh(new THREE.BoxGeometry(5.0, 0.1, 0.18), neonBorder);
  bottomBar.position.y = 3.05;
  g.add(bottomBar);
  // Side neon strips
  for (const sx of [-2.45, 2.45]) {
    const sideBar = new THREE.Mesh(new THREE.BoxGeometry(0.1, 2.4, 0.18), neonBorder);
    sideBar.position.set(sx, 4.2, 0);
    g.add(sideBar);
  }

  // Spotlight fixtures aiming at the ad
  for (const lx of [-1.6, 0, 1.6]) {
    const fixture = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.12, 0.15), _mat.darkMetal);
    fixture.position.set(lx, 5.45, 0.15);
    fixture.rotation.x = 0.4;
    g.add(fixture);
    const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.04, 4, 4),
      new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0xffeedd, emissiveIntensity: 0.5 }));
    bulb.position.set(lx, 5.42, 0.22);
    g.add(bulb);
  }

  return g;
}

function makeContainerStackMesh(): THREE.Group {
  const g = new THREE.Group();

  function shippingContainer(
    w: number, h: number, d: number,
    color: number, px: number, py: number, pz: number, rotY: number,
  ): void {
    const bodyMat = new THREE.MeshStandardMaterial({ color, metalness: 0.35, roughness: 0.5 });
    const frameMat = new THREE.MeshStandardMaterial({ color: 0x3a3e48, metalness: 0.5, roughness: 0.4 });

    // Main body
    const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), bodyMat);
    body.position.set(px, py + h / 2, pz);
    body.rotation.y = rotY;
    g.add(body);

    // Corner castings (8 corners)
    const castSize = 0.12;
    for (const cx of [-w / 2, w / 2]) {
      for (const cy of [-h / 2, h / 2]) {
        for (const cz of [-d / 2, d / 2]) {
          const cast = new THREE.Mesh(new THREE.BoxGeometry(castSize, castSize * 1.5, castSize), frameMat);
          cast.position.set(px + cx * Math.cos(rotY) - cz * Math.sin(rotY),
                           py + h / 2 + cy,
                           pz + cx * Math.sin(rotY) + cz * Math.cos(rotY));
          g.add(cast);
        }
      }
    }

    // Corrugation ridges on sides (vertical lines)
    for (let ri = -3; ri <= 3; ri++) {
      const ridge = new THREE.Mesh(new THREE.BoxGeometry(0.03, h - 0.3, 0.03), bodyMat);
      const rx = px + (ri * w / 8) * Math.cos(rotY) - (d / 2 + 0.02) * Math.sin(rotY);
      const rz = pz + (ri * w / 8) * Math.sin(rotY) + (d / 2 + 0.02) * Math.cos(rotY);
      ridge.position.set(rx, py + h / 2, rz);
      ridge.rotation.y = rotY;
      g.add(ridge);
    }

    // Door bars on end (2 vertical bars + handle)
    for (const db of [-0.25, 0.25]) {
      const bar = new THREE.Mesh(new THREE.BoxGeometry(0.04, h - 0.2, 0.04), frameMat);
      bar.position.set(
        px + (w / 2 + 0.02) * Math.cos(rotY) - db * Math.sin(rotY),
        py + h / 2,
        pz + (w / 2 + 0.02) * Math.sin(rotY) + db * Math.cos(rotY),
      );
      bar.rotation.y = rotY;
      g.add(bar);
    }
  }

  // Bottom container (rust orange)
  shippingContainer(3.5, 1.3, 1.8, 0xbf5b2a, 0, 0, 0, 0);

  // Top container (blue, slightly offset + rotated)
  shippingContainer(3.2, 1.1, 1.6, 0x2a6fbf, 0.15, 1.3, 0.05, 0.04);

  return g;
}

function makeGarageGateMesh(): THREE.Group {
  const g = new THREE.Group();
  const postMat = new THREE.MeshStandardMaterial({ color: 0x3a3f48, roughness: 0.62, metalness: 0.3 });
  const trackMat = new THREE.MeshStandardMaterial({ color: 0x555555, roughness: 0.5, metalness: 0.4 });

  // Side posts (thick steel)
  for (const sx of [-2.55, 2.55]) {
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.3, 3.2, 0.3), postMat);
    post.position.set(sx, 1.6, 0);
    g.add(post);
    // Track rail on inside of post
    const track = new THREE.Mesh(new THREE.BoxGeometry(0.06, 2.8, 0.1), trackMat);
    track.position.set(sx + (sx > 0 ? -0.18 : 0.18), 1.5, 0.05);
    g.add(track);
  }

  // Header beam across top
  const header = new THREE.Mesh(new THREE.BoxGeometry(5.4, 0.25, 0.3), postMat);
  header.position.set(0, 3.05, 0);
  g.add(header);

  // Roll-up gate panels (horizontal slats)
  const slotCount = 10;
  const slotH = 2.5 / slotCount;
  for (let i = 0; i < slotCount; i++) {
    const shade = 0.92 - (i % 2) * 0.06;
    const slotMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(shade * 0.5, shade * 0.55, shade * 0.6),
      metalness: 0.45, roughness: 0.42,
    });
    const slot = new THREE.Mesh(new THREE.BoxGeometry(4.8, slotH - 0.02, 0.08), slotMat);
    slot.position.set(0, slotH / 2 + i * slotH + 0.15, 0);
    g.add(slot);
    // Hinge line between panels
    if (i > 0) {
      const hinge = new THREE.Mesh(new THREE.BoxGeometry(4.8, 0.015, 0.1), _mat.darkMetal);
      hinge.position.set(0, i * slotH + 0.15, 0);
      g.add(hinge);
    }
  }

  // Handle at bottom center
  const handle = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.08, 0.06), _mat.chrome);
  handle.position.set(0, 0.35, 0.08);
  g.add(handle);

  // Motor housing (top right)
  const motor = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.4, 0.35), postMat);
  motor.position.set(2.0, 3.35, 0);
  g.add(motor);
  const motorCover = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.35, 8), _mat.darkMetal);
  motorCover.rotation.z = Math.PI / 2;
  motorCover.position.set(2.0, 3.35, 0.2);
  g.add(motorCover);

  // Warning light
  const light = new THREE.Mesh(
    new THREE.SphereGeometry(0.15, 8, 6),
    new THREE.MeshStandardMaterial({ color: 0xff4400, emissive: 0xff4400, emissiveIntensity: 0.9 }),
  );
  light.position.set(0, 3.3, 0.2);
  g.add(light);

  // Warning stripes on bottom panel
  for (let sx = -2.0; sx <= 2.0; sx += 0.5) {
    const stripe = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.2, 0.01), _mat.yellow);
    stripe.rotation.z = 0.5;
    stripe.position.set(sx, 0.25, 0.05);
    g.add(stripe);
  }

  return g;
}

export function isPursuitBreaker(kind: DestructibleKind): boolean {
  return (
    kind === 'gas_station' ||
    kind === 'water_tower' ||
    kind === 'scaffolding' ||
    kind === 'billboard' ||
    kind === 'container_stack' ||
    kind === 'garage_gate'
  );
}

function chaosBillDestroy(
  kind: DestructibleKind,
  pursuitBreaker: boolean,
  x: number,
  z: number,
): number {
  const j = rngFromPos(x + kind.length * 0.01, z);
  if (pursuitBreaker) {
    return 9200 + Math.floor(j * 6200);
  }
  switch (kind) {
    case 'gas_station':
      return 5800 + Math.floor(j * 2200);
    case 'water_tower':
      return 5200 + Math.floor(j * 1800);
    case 'scaffolding':
    case 'billboard':
      return 4100 + Math.floor(j * 1600);
    case 'container_stack':
    case 'garage_gate':
      return 4800 + Math.floor(j * 1900);
    case 'hydrant':
      return 2200 + Math.floor(j * 900);
    case 'vendor_cart':
      return 1600 + Math.floor(j * 700);
    case 'newsstand':
      return 1100 + Math.floor(j * 500);
    case 'cafe_set':
      return 1400 + Math.floor(j * 600);
    case 'barrier':
      return 900 + Math.floor(j * 400);
    case 'cone':
      return 350 + Math.floor(j * 250);
    case 'trash':
      return 220 + Math.floor(j * 180);
    default:
      return 700 + Math.floor(j * 500);
  }
}

function getDestructibleRadius(kind: DestructibleKind): number {
  switch (kind) {
    case 'cone': return CONE_R;
    case 'barrier': return BARRIER_R;
    case 'hydrant': return HYDRANT_R;
    case 'newsstand': return NEWS_R;
    case 'trash': return TRASH_R;
    case 'vendor_cart': return VENDOR_R;
    case 'cafe_set': return CAFE_R;
    case 'gas_station': return GAS_STATION_R;
    case 'water_tower': return WATER_TOWER_R;
    case 'scaffolding': return SCAFFOLD_R;
    case 'billboard': return BILLBOARD_R;
    case 'container_stack': return CONTAINER_STACK_R;
    case 'garage_gate': return GARAGE_GATE_R;
  }
}

function collectRoadSamples(rng: () => number): Array<{ x: number; z: number }> {
  const { HALF, GRID, ROAD_WIDTH, SIDEWALK_WIDTH } = MAP;
  const roadWithSidewalk = ROAD_WIDTH + SIDEWALK_WIDTH * 2;
  const totalRoadSpace = (GRID + 1) * roadWithSidewalk;
  const inner = MAP.SIZE - totalRoadSpace;
  const blockW = inner / GRID;
  const pad = 30;
  const out: Array<{ x: number; z: number }> = [];

  for (let i = 0; i <= GRID; i++) {
    const z = -HALF + i * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;
    for (let x = -HALF + pad; x < HALF - pad; x += 8 + rng() * 10) {
      out.push({ x: x + (rng() - 0.5) * 2, z: z + (rng() - 0.5) * (ROAD_WIDTH * 0.3) });
    }
  }
  for (let j = 0; j <= GRID; j++) {
    const x = -HALF + j * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;
    for (let z = -HALF + pad; z < HALF - pad; z += 8 + rng() * 10) {
      out.push({ x: x + (rng() - 0.5) * (ROAD_WIDTH * 0.3), z: z + (rng() - 0.5) * 2 });
    }
  }

  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

function pickKind(rng: () => number): DestructibleKind {
  const t = rng();
  if (t < 0.28) return 'cone';
  if (t < 0.48) return 'barrier';
  if (t < 0.62) return 'hydrant';
  if (t < 0.74) return 'newsstand';
  if (t < 0.87) return 'vendor_cart';
  if (t < 0.94) return 'cafe_set';
  return 'trash';
}

function pickPursuitBreakerKind(rng: () => number): DestructibleKind {
  const t = rng();
  if (t < 0.2) return 'gas_station';
  if (t < 0.38) return 'water_tower';
  if (t < 0.56) return 'scaffolding';
  if (t < 0.72) return 'billboard';
  if (t < 0.86) return 'container_stack';
  return 'garage_gate';
}

/**
 * prd2 Day 6 — cones, barriers, hydrants on roads; building-safe placement.
 */
export function createDestructibles(seed: number): {
  group: THREE.Group;
  instances: DestructibleInstance[];
} {
  const rng = mulberry32(seed ^ 0x9e3779b9);
  const boxes = getBuildingAABBs();
  const samples = collectRoadSamples(rng);
  const instances: DestructibleInstance[] = [];
  const group = new THREE.Group();
  group.name = 'Destructibles';

  const placed: Array<{ x: number; z: number; r: number }> = [];
  const minGap = 2.8;

  const tryPlace = (x: number, z: number, kind: DestructibleKind): void => {
    const radius = getDestructibleRadius(kind);
    if (isInsideAnyBuilding(x, z, radius + 0.2, boxes)) return;
    for (const p of placed) {
      if (Math.hypot(p.x - x, p.z - z) < p.r + radius + minGap) return;
    }

    let root: THREE.Group;
    let health: number;
    let slowdown = 0;
    if (kind === 'cone') {
      root = makeConeMesh();
      health = 1;
    } else if (kind === 'barrier') {
      root = makeBarrierMesh();
      health = 2;
      slowdown = 0.15;
    } else if (kind === 'hydrant') {
      root = makeHydrantMesh();
      health = 2;
    } else if (kind === 'newsstand') {
      root = makeNewsstandMesh();
      health = 1;
      slowdown = 0.08;
    } else if (kind === 'vendor_cart') {
      root = makeVendorCartMesh();
      health = 2;
      slowdown = 0.11;
    } else if (kind === 'cafe_set') {
      root = makeCafeSetMesh();
      health = 1;
      slowdown = 0.06;
    } else if (kind === 'gas_station') {
      root = makeGasStationMesh();
      health = 4;
      slowdown = 0.24;
    } else if (kind === 'water_tower') {
      root = makeWaterTowerBreakerMesh();
      health = 4;
      slowdown = 0.2;
    } else if (kind === 'scaffolding') {
      root = makeScaffoldingMesh();
      health = 3;
      slowdown = 0.18;
    } else if (kind === 'billboard') {
      root = makeBillboardMesh();
      health = 3;
      slowdown = 0.16;
    } else if (kind === 'container_stack') {
      root = makeContainerStackMesh();
      health = 3;
      slowdown = 0.2;
    } else if (kind === 'garage_gate') {
      root = makeGarageGateMesh();
      health = 3;
      slowdown = 0.24;
    } else {
      root = makeTrashMesh();
      health = 1;
    }

    const yaw = rng() * Math.PI * 2;
    root.position.set(x, 0, z);
    root.rotation.y = yaw;
    group.add(root);

    placed.push({ x, z, r: radius });
    instances.push({
      kind,
      x,
      z,
      radius,
      health,
      maxHealth: health,
      slowdown,
      alive: true,
      root,
      tipped: false,
      touchingCar: false,
    });
  };

  /**
   * Pursuit breakers must be placed **before** ambient props. Large radii (~2–3m) + minGap
   * meant almost no valid spots remained after filling 140 cones/barriers/hydrants first.
   */
  const breakerTarget = 12;
  let breakerCount = 0;
  for (const s of samples) {
    if (breakerCount >= breakerTarget) break;
    const k = pickPursuitBreakerKind(rng);
    const before = instances.length;
    tryPlace(s.x, s.z, k);
    if (instances.length > before) {
      breakerCount++;
      continue;
    }
    for (let j = 0; j < 8; j++) {
      const jx = s.x + (rng() - 0.5) * 22;
      const jz = s.z + (rng() - 0.5) * 22;
      const mid = instances.length;
      tryPlace(jx, jz, k);
      if (instances.length > mid) {
        breakerCount++;
        break;
      }
    }
  }

  let n = instances.length;
  const target = 80;
  for (const s of samples) {
    if (n >= target) break;
    const k = pickKind(rng);
    tryPlace(s.x, s.z, k);
    n = instances.length;
  }

  return { group, instances };
}

function rngFromPos(x: number, z: number): number {
  const s = Math.sin(x * 12.9898 + z * 78.233) * 43758.5453;
  return s - Math.floor(s);
}

/**
 * Circle vs props: push-out, damage from impact speed, barrier slowdown.
 * Call after building collision resolve.
 */
export function resolveDestructibleCollisions(
  x: number,
  z: number,
  vx: number,
  vz: number,
  instances: DestructibleInstance[],
  onHit: (e: DestructibleHitEvent) => void,
): { x: number; z: number; vx: number; vz: number } {
  const cr = CAR.COLLISION_RADIUS;
  let px = x;
  let pz = z;
  let pvx = vx;
  let pvz = vz;

  for (const d of instances) {
    if (!d.alive) continue;
    const dx = px - d.x;
    const dz = pz - d.z;
    const dist = Math.hypot(dx, dz);
    const minD = cr + d.radius;

    if (dist >= minD + 0.02) {
      d.touchingCar = false;
      continue;
    }
    if (dist < 1e-5) continue;

    const nx = dx / dist;
    const nz = dz / dist;
    const pen = Math.max(0, minD - dist);
    px += nx * pen;
    pz += nz * pen;

    const vDot = pvx * nx + pvz * nz;
    const impact = Math.max(0, -vDot + Math.abs(pvx) * 0.06 + Math.abs(pvz) * 0.06);

    if ((d.kind === 'barrier' || d.kind === 'newsstand') && d.slowdown > 0) {
      pvx *= 1 - d.slowdown * 0.85;
      pvz *= 1 - d.slowdown * 0.85;
    }

    const dot = pvx * nx + pvz * nz;
    if (dot < 0) {
      pvx -= nx * dot * 1.15;
      pvz -= nz * dot * 1.15;
    }

    const entry = !d.touchingCar;
    d.touchingCar = true;

    if (entry) {
      const dmg = isPursuitBreaker(d.kind)
        ? 0.4 + impact * 0.09
        : d.kind === 'cone' || d.kind === 'trash'
          ? Math.max(0.85, 0.45 + impact * 0.14)
          : 0.5 + impact * 0.11;

      d.health -= dmg;

      if (
        !d.tipped &&
        (
          d.kind === 'cone' ||
          d.kind === 'barrier' ||
          d.kind === 'newsstand' ||
          d.kind === 'vendor_cart' ||
          d.kind === 'cafe_set' ||
          d.kind === 'scaffolding' ||
          d.kind === 'billboard' ||
          d.kind === 'container_stack' ||
          d.kind === 'garage_gate'
        )
      ) {
        d.tipped = true;
        d.root.rotation.x =
          d.kind === 'cone'
            ? -0.85
            : d.kind === 'barrier'
              ? -0.35
              : d.kind === 'vendor_cart'
                ? -0.45
                : d.kind === 'cafe_set'
                  ? -0.65
                  : d.kind === 'scaffolding' || d.kind === 'billboard'
                    ? -0.42
                    : d.kind === 'container_stack' || d.kind === 'garage_gate'
                      ? -0.28
                      : -0.55;
        d.root.rotation.z = (rngFromPos(d.x, d.z) - 0.5) * 0.4;
      }

      if (d.health <= 0 && d.alive) {
        d.alive = false;
        d.root.visible = false;
        const breaker = isPursuitBreaker(d.kind);
        onHit({
          kind: d.kind,
          x: d.x,
          z: d.z,
          destroyed: true,
          waterBurst: d.kind === 'hydrant',
          pursuitBreaker: breaker,
          shockRadius: breaker ? 20 + d.radius * 3 : 0,
          chaosBill: chaosBillDestroy(d.kind, breaker, d.x, d.z),
        });
      } else {
        onHit({
          kind: d.kind,
          x: d.x,
          z: d.z,
          destroyed: false,
          waterBurst: false,
          pursuitBreaker: false,
          shockRadius: 0,
          chaosBill: Math.min(3800, Math.floor(160 + impact * 240)),
        });
      }
    }
  }

  return { x: px, z: pz, vx: pvx, vz: pvz };
}

export function spawnDestructibleBurst(
  scene: THREE.Scene,
  x: number,
  y: number,
  z: number,
  kind: DestructibleKind,
  waterBurst: boolean,
): void {
  const brk = isPursuitBreaker(kind);
  const n = brk ? 28 : kind === 'hydrant' && waterBurst ? 18 : 12;
  const positions = new Float32Array(n * 3);
  const velocities: THREE.Vector3[] = [];
  const c1 = waterBurst
    ? 0x88ccff
    : kind === 'cone'
      ? 0xff8833
      : kind === 'gas_station'
        ? 0xff5533
        : kind === 'water_tower'
          ? 0xbbddff
          : kind === 'scaffolding'
            ? 0xc7ccd6
            : kind === 'billboard'
              ? 0x66d8ff
              : kind === 'container_stack'
                ? 0xd87c3d
                : kind === 'garage_gate'
                  ? 0x9aa4b8
                  : kind === 'vendor_cart'
                    ? 0xffd26e
                    : kind === 'cafe_set'
                      ? 0xb78a5e
      : kind === 'trash'
        ? 0x8899aa
        : kind === 'newsstand'
          ? 0xddb8a0
          : 0xf5e040;
  const c2 = waterBurst ? 0xffffff : kind === 'trash' ? 0xccd0d8 : 0xffaa66;

  const spread = brk ? 18 : 10;
  const upForce = brk ? 14 : waterBurst ? 6 : 2;
  const upRange = brk ? 12 : waterBurst ? 9 : 7;
  for (let i = 0; i < n; i++) {
    positions[i * 3] = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;
    const vx = (Math.random() - 0.5) * spread;
    const vy = upForce + Math.random() * upRange;
    const vz = (Math.random() - 0.5) * spread;
    velocities.push(new THREE.Vector3(vx, vy, vz));
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const hex = i % 3 === 0 ? c1 : c2;
    const r = ((hex >> 16) & 0xff) / 255;
    const g = ((hex >> 8) & 0xff) / 255;
    const b = (hex & 0xff) / 255;
    col[i * 3] = r;
    col[i * 3 + 1] = g;
    col[i * 3 + 2] = b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3));

  const mat = new THREE.PointsMaterial({
    size: brk ? 0.7 : waterBurst ? 0.55 : 0.38,
    vertexColors: true,
    transparent: true,
    opacity: 0.95,
    depthWrite: false,
  });
  const pts = new THREE.Points(geo, mat);
  pts.position.set(0, 0, 0);
  scene.add(pts);

  // Explosion flash — emissive sphere instead of PointLight
  let flashMesh: THREE.Mesh | null = null;
  if (brk) {
    const flashColor = kind === 'gas_station' ? 0xff6622 : kind === 'water_tower' ? 0x88ccff : 0xffaa44;
    flashMesh = new THREE.Mesh(
      new THREE.SphereGeometry(3, 6, 6),
      new THREE.MeshBasicMaterial({ color: flashColor, transparent: true, opacity: 0.8 }),
    );
    flashMesh.position.set(x, y + 3, z);
    scene.add(flashMesh);
  }

  const t0 = performance.now();
  const lifeMs = brk ? 600 : waterBurst ? 400 : 280;
  let prev = t0;

  function tick(now: number): void {
    const dt = Math.min(0.05, (now - prev) / 1000);
    prev = now;
    const elapsed = now - t0;
    const t = elapsed / lifeMs;
    if (t >= 1) {
      scene.remove(pts);
      geo.dispose();
      mat.dispose();
      if (flashMesh) {
        scene.remove(flashMesh);
        (flashMesh.material as THREE.MeshBasicMaterial).dispose();
        flashMesh.geometry.dispose();
      }
      return;
    }
    const g = geo.attributes.position;
    const arr = g.array as Float32Array;
    const gAcc = 22;
    for (let i = 0; i < n; i++) {
      const v = velocities[i]!;
      v.y -= gAcc * dt;
      arr[i * 3] += v.x * dt;
      arr[i * 3 + 1] += v.y * dt;
      arr[i * 3 + 2] += v.z * dt;
    }
    g.needsUpdate = true;
    mat.opacity = 0.95 * (1 - t);
    if (flashMesh) {
      const flashMat = flashMesh.material as THREE.MeshBasicMaterial;
      flashMat.opacity = 0.8 * Math.max(0, 1 - t * 2.5);
      flashMesh.scale.setScalar(1 + t * 3);
    }
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}
